import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import PortalLayout from '../../components/portal/PortalLayout';
import portalApi from '../../services/portalApi';
import { Briefcase, FileText, Calendar, DollarSign, Loader } from 'lucide-react';

const PortalDashboard = () => {
  const navigate = useNavigate();
  const { customer, token, isAuthenticated } = usePortalContext();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    upcomingJobs: 0,
    completedJobs: 0,
    unpaidInvoices: 0,
    totalSpent: 0
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
  try {
    setLoading(true);
    const [jobs, invoices] = await Promise.all([
      portalApi.getJobs(token),
      portalApi.getInvoices(token)
    ]);

    // Calculate stats
    const upcoming = jobs.filter(j => {
      // Show Scheduled and Quoted jobs as "upcoming"
      if (j.status === 'Scheduled' || j.status === 'Quoted') {
        // If there's a scheduled date, make sure it's in the future
        // if (j.scheduledDate) {
        //   return new Date(j.scheduledDate) >= new Date();
        // }
        // If no scheduled date yet (Quoted status), still count as upcoming
        return true;
      }
      return false;
    }).length;

    const completed = jobs.filter(j => j.status === 'Completed').length;

    const unpaid = invoices.filter(i => !i.isPaid).length;

    const totalSpent = invoices
      .filter(i => i.isPaid)
      .reduce((sum, i) => sum + i.total, 0);

    setStats({
      upcomingJobs: upcoming,
      completedJobs: completed,
      unpaidInvoices: unpaid,
      totalSpent: totalSpent
    });
  } catch (error) {
    console.error('Error loading dashboard:', error);
  } finally {
    setLoading(false);
  }
};

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader size={48} className="text-primary-600 animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Welcome back, {customer?.name}!
          </h2>
          <p className="text-gray-600 mt-2">
            Here's an overview of your account activity
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Upcoming Jobs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Upcoming Jobs</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.upcomingJobs}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="text-blue-600" size={24} />
              </div>
            </div>
            <button
              onClick={() => navigate('/portal/jobs')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-4"
            >
              View jobs →
            </button>
          </div>

          {/* Completed Jobs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.completedJobs}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Briefcase className="text-green-600" size={24} />
              </div>
            </div>
            <button
              onClick={() => navigate('/portal/jobs')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-4"
            >
              View history →
            </button>
          </div>

          {/* Unpaid Invoices */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Unpaid Invoices</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.unpaidInvoices}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <FileText className="text-amber-600" size={24} />
              </div>
            </div>
            <button
              onClick={() => navigate('/portal/invoices')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-4"
            >
              View invoices →
            </button>
          </div>

          {/* Total Spent */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Spent</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  ${stats.totalSpent.toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <DollarSign className="text-purple-600" size={24} />
              </div>
            </div>
            <button
              onClick={() => navigate('/portal/invoices')}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-4"
            >
              View details →
            </button>
          </div>
        </div>

        {/* Quick Actions */}
<div className="bg-white rounded-lg shadow p-6">
  <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <button
      onClick={() => navigate('/portal/jobs')}
      className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
    >
      <Briefcase size={18} />
      View My Jobs
    </button>
    <button
      onClick={() => navigate('/portal/invoices')}
      className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
    >
      <FileText size={18} />
      View Invoices
    </button>
    <button
      onClick={() => navigate('/portal/request-quote')}
      className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
    >
      Request New Service
    </button>
  </div>
</div>

        {/* Contact Info */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Need Help?</h3>
          <p className="text-sm text-gray-600">
            If you have any questions or need assistance, please don't hesitate to contact us.
          </p>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalDashboard;